/**
 * useModuleState - Consolidated module and segment state management
 *
 * Manages all module-related states including:
 * - Module data per polygon
 * - Segment data per polygon
 * - Module configuration (dimensions, spacing, etc.)
 * - Selection and editing states
 * - Drawing/placement modes
 */

import { useState, useCallback } from 'react';
import type { SolarModule, ModuleSegment } from '../../../types/geotiff.types';

export interface SegmentSelection {
  polygonId: number;
  index: number;
}

export interface ModuleSelection {
  polygonId: number;
  index: number;
}

export interface DraggingHandle {
  type: 'rotation' | 'corner' | 'edge';
  polygonId: number;
  segmentIndex: number;
  handleIndex?: number;
}

export interface ModuleConfig {
  moduleWidth: number;
  moduleHeight: number;
  moduleOrientation: 'portrait' | 'landscape';
  modulePowerWatts: number;
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  targetModuleCount: number | null;
  targetFillPercentage: number;
  edgeSetback: number;
  obstructionSetback: number;
}

export interface ModuleState {
  // Core data
  modules: { [polygonId: number]: SolarModule[] };
  moduleSegments: { [polygonId: number]: ModuleSegment[] };

  // Segment selection/editing
  selectedSegment: SegmentSelection | null;
  editingSegmentVertices: SegmentSelection | null;
  isDrawingSegment: boolean;

  // Free-field mode
  isFreeFieldMode: boolean;
  freeFieldAzimuth: number;
  freeFieldPolygonVertices: [number, number][];

  // Segment manipulation
  draggingHandle: DraggingHandle | null;
  isRotatingSegment: boolean;
  isResizingSegmentCorner: boolean;
  isStretchingSegmentEdge: boolean;
  isDraggingSegment: boolean;

  // Module configuration
  moduleWidth: number;
  moduleHeight: number;
  moduleOrientation: 'portrait' | 'landscape';
  modulePowerWatts: number;
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  targetModuleCount: number | null;
  targetFillPercentage: number;
  edgeSetback: number;
  obstructionSetback: number;

  // Module selection/placement
  isPlacingModule: boolean;
  selectedModule: ModuleSelection | null;
  selectedModules: Set<number>;
  previewModules: SolarModule[];
  isSelectingModules: boolean;
  selectionBoxStart: [number, number] | null;
  selectionBox: [number, number][] | null;

  // Edit mode
  editMode: 'site' | 'system';
}

export interface ModuleStateActions {
  // Core data setters
  setModules: React.Dispatch<React.SetStateAction<{ [polygonId: number]: SolarModule[] }>>;
  setModuleSegments: React.Dispatch<React.SetStateAction<{ [polygonId: number]: ModuleSegment[] }>>;

  // Segment selection/editing setters
  setSelectedSegment: (selection: SegmentSelection | null) => void;
  setEditingSegmentVertices: (selection: SegmentSelection | null) => void;
  setIsDrawingSegment: (value: boolean) => void;

  // Free-field mode setters
  setIsFreeFieldMode: (value: boolean) => void;
  setFreeFieldAzimuth: (value: number) => void;
  setFreeFieldPolygonVertices: React.Dispatch<React.SetStateAction<[number, number][]>>;

  // Segment manipulation setters
  setDraggingHandle: (handle: DraggingHandle | null) => void;
  setIsRotatingSegment: (value: boolean) => void;
  setIsResizingSegmentCorner: (value: boolean) => void;
  setIsStretchingSegmentEdge: (value: boolean) => void;
  setIsDraggingSegment: (value: boolean) => void;

  // Module configuration setters
  setModuleWidth: (value: number) => void;
  setModuleHeight: (value: number) => void;
  setModuleOrientation: (value: 'portrait' | 'landscape') => void;
  setModulePowerWatts: (value: number) => void;
  setModuleSpacing: (value: number) => void;
  setRowSpacingMultiplier: (value: number) => void;
  setTargetModuleCount: (value: number | null) => void;
  setTargetFillPercentage: (value: number) => void;
  setEdgeSetback: (value: number) => void;
  setObstructionSetback: (value: number) => void;

  // Module selection/placement setters
  setIsPlacingModule: (value: boolean) => void;
  setSelectedModule: (selection: ModuleSelection | null) => void;
  setSelectedModules: React.Dispatch<React.SetStateAction<Set<number>>>;
  setPreviewModules: React.Dispatch<React.SetStateAction<SolarModule[]>>;
  setIsSelectingModules: (value: boolean) => void;
  setSelectionBoxStart: (pos: [number, number] | null) => void;
  setSelectionBox: (box: [number, number][] | null) => void;

  // Edit mode setter
  setEditMode: (mode: 'site' | 'system') => void;

  // Convenience methods
  getModuleConfig: () => ModuleConfig;
  clearModuleSelection: () => void;
  clearSegmentSelection: () => void;
  startDrawingSegment: (freeField?: boolean) => void;
  stopDrawingSegment: () => void;
}

// Special ID for free-field segments (not tied to any roof)
export const FREE_FIELD_POLYGON_ID = -1;

export function useModuleState(): ModuleState & ModuleStateActions {
  // Core data
  const [modules, setModules] = useState<{ [polygonId: number]: SolarModule[] }>({});
  const [moduleSegments, setModuleSegments] = useState<{ [polygonId: number]: ModuleSegment[] }>({});

  // Segment selection/editing
  const [selectedSegment, setSelectedSegment] = useState<SegmentSelection | null>(null);
  const [editingSegmentVertices, setEditingSegmentVertices] = useState<SegmentSelection | null>(null);
  const [isDrawingSegment, setIsDrawingSegment] = useState(false);

  // Free-field mode
  const [isFreeFieldMode, setIsFreeFieldMode] = useState(false);
  const [freeFieldAzimuth, setFreeFieldAzimuth] = useState(180);
  const [freeFieldPolygonVertices, setFreeFieldPolygonVertices] = useState<[number, number][]>([]);

  // Segment manipulation
  const [draggingHandle, setDraggingHandle] = useState<DraggingHandle | null>(null);
  const [isRotatingSegment, setIsRotatingSegment] = useState(false);
  const [isResizingSegmentCorner, setIsResizingSegmentCorner] = useState(false);
  const [isStretchingSegmentEdge, setIsStretchingSegmentEdge] = useState(false);
  const [isDraggingSegment, setIsDraggingSegment] = useState(false);

  // Module configuration
  const [moduleWidth, setModuleWidth] = useState(1.134);
  const [moduleHeight, setModuleHeight] = useState(2.382);
  const [moduleOrientation, setModuleOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [modulePowerWatts, setModulePowerWatts] = useState(625);
  const [moduleSpacing, setModuleSpacing] = useState(0.02);
  const [rowSpacingMultiplier, setRowSpacingMultiplier] = useState(1.3);
  const [targetModuleCount, setTargetModuleCount] = useState<number | null>(null);
  const [targetFillPercentage, setTargetFillPercentage] = useState(50);
  const [edgeSetback, setEdgeSetback] = useState(0.02);
  const [obstructionSetback, setObstructionSetback] = useState(0.3);

  // Module selection/placement
  const [isPlacingModule, setIsPlacingModule] = useState(false);
  const [selectedModule, setSelectedModule] = useState<ModuleSelection | null>(null);
  const [selectedModules, setSelectedModules] = useState<Set<number>>(new Set());
  const [previewModules, setPreviewModules] = useState<SolarModule[]>([]);
  const [isSelectingModules, setIsSelectingModules] = useState(false);
  const [selectionBoxStart, setSelectionBoxStart] = useState<[number, number] | null>(null);
  const [selectionBox, setSelectionBox] = useState<[number, number][] | null>(null);

  // Edit mode
  const [editMode, setEditMode] = useState<'site' | 'system'>('system');

  // Convenience methods
  const getModuleConfig = useCallback((): ModuleConfig => ({
    moduleWidth,
    moduleHeight,
    moduleOrientation,
    modulePowerWatts,
    moduleSpacing,
    rowSpacingMultiplier,
    targetModuleCount,
    targetFillPercentage,
    edgeSetback,
    obstructionSetback,
  }), [
    moduleWidth, moduleHeight, moduleOrientation, modulePowerWatts,
    moduleSpacing, rowSpacingMultiplier, targetModuleCount, targetFillPercentage,
    edgeSetback, obstructionSetback
  ]);

  const clearModuleSelection = useCallback(() => {
    setSelectedModule(null);
    setSelectedModules(new Set());
    setIsSelectingModules(false);
    setSelectionBoxStart(null);
    setSelectionBox(null);
  }, []);

  const clearSegmentSelection = useCallback(() => {
    setSelectedSegment(null);
    setEditingSegmentVertices(null);
    setDraggingHandle(null);
  }, []);

  const startDrawingSegment = useCallback((freeField: boolean = false) => {
    setIsDrawingSegment(true);
    setIsFreeFieldMode(freeField);
    setPreviewModules([]);
    setFreeFieldPolygonVertices([]);
  }, []);

  const stopDrawingSegment = useCallback(() => {
    setIsDrawingSegment(false);
    setPreviewModules([]);
    setFreeFieldPolygonVertices([]);
  }, []);

  return {
    // State
    modules,
    moduleSegments,
    selectedSegment,
    editingSegmentVertices,
    isDrawingSegment,
    isFreeFieldMode,
    freeFieldAzimuth,
    freeFieldPolygonVertices,
    draggingHandle,
    isRotatingSegment,
    isResizingSegmentCorner,
    isStretchingSegmentEdge,
    isDraggingSegment,
    moduleWidth,
    moduleHeight,
    moduleOrientation,
    modulePowerWatts,
    moduleSpacing,
    rowSpacingMultiplier,
    targetModuleCount,
    targetFillPercentage,
    edgeSetback,
    obstructionSetback,
    isPlacingModule,
    selectedModule,
    selectedModules,
    previewModules,
    isSelectingModules,
    selectionBoxStart,
    selectionBox,
    editMode,

    // Setters
    setModules,
    setModuleSegments,
    setSelectedSegment,
    setEditingSegmentVertices,
    setIsDrawingSegment,
    setIsFreeFieldMode,
    setFreeFieldAzimuth,
    setFreeFieldPolygonVertices,
    setDraggingHandle,
    setIsRotatingSegment,
    setIsResizingSegmentCorner,
    setIsStretchingSegmentEdge,
    setIsDraggingSegment,
    setModuleWidth,
    setModuleHeight,
    setModuleOrientation,
    setModulePowerWatts,
    setModuleSpacing,
    setRowSpacingMultiplier,
    setTargetModuleCount,
    setTargetFillPercentage,
    setEdgeSetback,
    setObstructionSetback,
    setIsPlacingModule,
    setSelectedModule,
    setSelectedModules,
    setPreviewModules,
    setIsSelectingModules,
    setSelectionBoxStart,
    setSelectionBox,
    setEditMode,

    // Convenience methods
    getModuleConfig,
    clearModuleSelection,
    clearSegmentSelection,
    startDrawingSegment,
    stopDrawingSegment,
  };
}

export default useModuleState;
